/**
 * Rejection Notice Component
 * Shows users what fields were rejected and need correction
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { verificationPersistence } from '@/utils/verificationPersistence';

interface RejectionNoticeProps {
  userId: string;
  onDismiss?: () => void;
}

export const RejectionNotice: React.FC<RejectionNoticeProps> = ({ userId, onDismiss }) => {
  const [expanded, setExpanded] = React.useState(true);
  const rejectionInfo = verificationPersistence.getRejectedFieldInfo(userId);

  if (!rejectionInfo || !verificationPersistence.hasRecentRejection(userId)) {
    return null;
  }

  const daysSinceRejection = Math.floor(
    (Date.now() - new Date(rejectionInfo.rejectedAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Alert 
      severity="warning" 
      sx={{ mb: 3 }}
      action={
        <IconButton
          aria-label="toggle details"
          size="small"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      }
    >
      <AlertTitle>
        Your previous verification was rejected {daysSinceRejection === 0 ? 'today' : `${daysSinceRejection} days ago`}
      </AlertTitle>
      
      <Collapse in={expanded}>
        <Box>
          <Typography variant="body2" gutterBottom>
            <strong>Reason:</strong> {rejectionInfo.rejectionReason}
          </Typography>
          
          {rejectionInfo.rejectedFields && rejectionInfo.rejectedFields.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Fields that need correction:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {rejectionInfo.rejectedFields.map((field) => (
                  <Chip
                    key={field}
                    label={field}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          
          <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
            Please update the highlighted fields and resubmit your verification. 
            Your previous data has been preserved where possible.
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  );
};